import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import {
  getTrxReport, IGetTrxReportData, postTrxReport, TrxReportSummaryI,
} from "../../../services/trxReportService";
import { getMasterDepartment } from "../../../services/masterDepartmentService";
import { dateToFormatedStringDate } from "../../../utils/common";
import useAuth from "../../../components/Authentication/useAuth";
import { getDetailTrx } from "../../../services/trxService";
import { putTrxApproval } from "../../../services/trxApprovalService";
import { IOptionItem } from "../../../typings/common";
import { columnOptions } from "./constants";
import { useDebounce } from "../../../hooks";

export interface FilterI {
  company?: {
    label: string,
    value: string,
  },
  companyAccount?: {
    label: string,
    value: string,
  },
  department?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  dates?: Date[],
  search?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

const initSummary = {
  total_bon_amount: "0",
  bon_amount_terbesar: "0",
  bon_amount_terkecil: "0",
};

const useReportReceiptSubmission = () => {
  const { userInfo, userAccount } = useAuth();
  const snackbar = useSnackbar();

  const initPool = userAccount.userPools?.length === 1
    ? { label: userAccount.userPools[0].poolName, value: userAccount.userPools[0].poolId }
    : undefined;
  const [params, setParams] = useState<FilterI>({
    sortBy: "trx_date",
    sortType: "DSC",
    dates: [new Date(), new Date()],
    pool: initPool,
    department: { label: "Semua departemen", value: "" },
  });
  const [selectedTrxs, setSelectedTrxs] = useState<IGetTrxReportData[]>([]);

  const [companyInput, setCompanyInput] = useState("");
  const [companyAccountInput, setCompanyAccountInput] = useState("");
  const [summary, setSummary] = useState<TrxReportSummaryI>(initSummary);
  const [modalConfig, setModalConfig] = useState<{ id: string, extendBm: string, type: "DETAIL" | "REJECT" | "APPROVE" }>();

  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });

  const [isLoading, setLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [columnOpts, setColumnOpts] = useState<IOptionItem[]>(JSON.parse(localStorage.getItem("COLUMN_REPORT") || JSON.stringify(columnOptions)));
  const [hiddenColumn, setHiddenColumn] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  useEffect(() => {
    setRejectReason("");
  }, [modalConfig]);

  useEffect(() => {
    let isAllHidden = true;
    const hiddenCol = columnOpts.map((opt) => {
      let val = "";
      if (!opt.checked) val = opt.value.toString();
      else isAllHidden = false;
      return val;
    });
    if (isAllHidden) {
      setColumnOpts(columnOptions);
      localStorage.setItem("COLUMN_REPORT", JSON.stringify(columnOptions));
      setHiddenColumn([]);
    } else {
      localStorage.setItem("COLUMN_REPORT", JSON.stringify(columnOpts));
      setHiddenColumn(hiddenCol);
    }
  }, [columnOpts]);

  const fetchList = async (pageParam: number) => {
    const startDate = params.dates && params.dates[0];
    const endDate = params.dates && params.dates[1];
    const formatedStartDate = dateToFormatedStringDate(startDate);
    const formatedEndDate = dateToFormatedStringDate(endDate);
    const sortBy = params.sortType === "DEFAULT" ? "trx_date" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    return getTrxReport({
      department: params.department?.value,
      pool_id: params.pool?.value,
      page_number: pageParam,
      page_size: 10,
      search: params.search,
      start_date: formatedStartDate,
      end_date: formatedEndDate,
      sort_by: sortBy,
      sort_type: sortType,
      employee_email: userInfo.email,
    }).then((res) => {
      setSelectedTrxs([]);
      if (res.metadata) setMeta(res.metadata);
      if (res.summary) setSummary(res.summary);
      return res.list_trx_report;
    }).catch(() => []);
  };

  const {
    data: trxReportPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
  } = useInfiniteQuery(
    ["trxReportList", params],
    async ({ pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const trxReportList = useMemo(
    () => trxReportPages.pages.flatMap((page) => page),
    [trxReportPages],
  );

  const { data: dataMasterDepartment } = useQuery({
    queryKey: "get_master_department",
    queryFn: () => getMasterDepartment(),
  });
  const { data: dataDetailTrx } = useQuery({
    queryKey: modalConfig?.id,
    enabled: !!modalConfig?.id,
    queryFn: () => (modalConfig ? getDetailTrx(modalConfig.id, modalConfig.extendBm) : undefined),
  });

  const handleTrxCheck = (row: IGetTrxReportData) => {
    const trxId = row.trx_id;
    const trxIndex = selectedTrxs.findIndex((val) => val.trx_id === trxId);

    if (trxIndex >= 0) {
      setSelectedTrxs((curr) => {
        const newTrxs = [...curr];
        newTrxs.splice(trxIndex, 1);
        return newTrxs;
      });
    } else setSelectedTrxs((curr) => [...curr, row]);
  };

  const handleHeaderTrxcheck = () => {
    if (selectedTrxs.length === trxReportList.length) {
      setSelectedTrxs([]);
    } else setSelectedTrxs(trxReportList.map((item) => item));
  };

  const handlePushToHeadOffice = () => {
    if (modalConfig?.id) {
      setLoading(true);
      postTrxReport({ trx_id: modalConfig.id, create_by: userInfo.preferred_username })
        .then((res) => {
          if (res.responseCode === "Succeed") {
            refetchTable();
            setModalConfig(undefined);
            setSummary(initSummary);
            snackbar.show("Berhasil push data", "success");
          } else snackbar.show(res.responseMessage || "Gagal push data", "error");
        })
        .catch((error) => {
          console.error(error);
          snackbar.show(error.message || JSON.stringify(error), "error");
        })
        .finally(() => setLoading(false));
    }
  };

  const handleReject = () => {
    if (modalConfig?.id && dataDetailTrx) {
      setLoading(true);
      putTrxApproval({
        trx_id: dataDetailTrx.trx_id,
        approval_number: dataDetailTrx.trx_approvals.find((approval) => approval.status_approval === "WAITING")?.approval_number.toString() || "0",
        status_approval: "REJECTED",
        employee_email: userInfo.email,
        reason_status: rejectReason,
        update_by: userInfo.preferred_username,
      })
        .then((res) => {
          if (res.responseCode === "Succeed") {
            refetchTable();
            setRejectReason("");
            setModalConfig(undefined);
            setSummary(initSummary);
            snackbar.show("Berhasil tolak pengajuan", "success");
          } else snackbar.show(res.responseMessage || "Gagal tolak pengajuan", "error");
        })
        .catch(() => snackbar.show("Gagal tolak pengajuan", "error"))
        .finally(() => setLoading(false));
    }
  };

  return {
    selectedTrxs,
    setSelectedTrxs,
    params,
    setParams,
    searchText,
    setSearchText,
    companyInput,
    setCompanyInput,
    companyAccountInput,
    setCompanyAccountInput,
    isLoading,
    meta,
    setMeta,
    summary,
    modalConfig,
    setModalConfig,
    rejectReason,
    setRejectReason,
    columnOpts,
    setColumnOpts,
    hiddenColumn,

    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    refetchTable,
    trxReportList,
    dataMasterDepartment,
    dataDetailTrx,
    handleTrxCheck,
    handleHeaderTrxcheck,
    handlePushToHeadOffice,
    handleReject,
  };
};

export default useReportReceiptSubmission;
